import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
  Skeleton,
  Chip,
  Stack,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
} from '@mui/icons-material';
import { PptxFile } from '../../types';
import { useLang } from '../../contexts/langContext';

interface SlideshowCardProps {
  file: PptxFile;
  /** Optional list of tag names corresponding to file.tagIds */
  tagNames?: string[];
  onClick: () => void;
  onContextMenu: (event: React.MouseEvent) => void;
  onMoreClick: (event: React.MouseEvent) => void;
}

function SlideshowCard({ file, tagNames = [], onClick, onContextMenu, onMoreClick }: SlideshowCardProps) {
  const { lang } = useLang();
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const maxSlides = file.parsedSlides?.length || 0;

  // Split filename into name and extension
  const filenameParts = file.filename.match(/(.+)(\.[^.]+)$/);
  const name = filenameParts ? filenameParts[1] : file.filename;
  const ext = filenameParts ? filenameParts[2] : '';

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handlePrevSlide = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    const nextSlide = currentSlide > 1 ? currentSlide - 1 : maxSlides;
    
    setTimeout(() => {
      setCurrentSlide(nextSlide);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 150);
  }, [currentSlide, maxSlides, isTransitioning]);

  const handleNextSlide = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    const nextSlide = (currentSlide % maxSlides) + 1;
    
    setTimeout(() => {
      setCurrentSlide(nextSlide);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 150);
  }, [currentSlide, maxSlides, isTransitioning]);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (!img.getAttribute('data-error')) {
      img.setAttribute('data-error', 'true');
      img.src = '/images/placeholder-slide.jpg';
      setHasError(true);
    }
    setIsLoading(false);
  }, []);

  return (
    <Card
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      onContextMenu={onContextMenu}
      elevation={isHovered ? 4 : 1}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          pt: '56.25%', // 16:9 aspect ratio
          bgcolor: 'grey.100',
        }}
      >
        {isLoading && (
          <Skeleton
            variant="rectangular"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          />
        )}
        <CardMedia
          component="img"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 0.15s ease-in-out',
            opacity: isLoading || isTransitioning ? 0 : 1,
          }}
          image={`/api/files/thumbnails/${file._id}/${currentSlide}.jpg`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          alt={`Slide ${currentSlide} of ${file.filename}`}
        />
        {isHovered && maxSlides > 1 && (
          <>
            <IconButton
              size="large"
              onClick={handlePrevSlide}
              disabled={isTransitioning}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.4)',
                color: 'white',
                backdropFilter: 'blur(4px)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              size="large"
              onClick={handleNextSlide}
              disabled={isTransitioning}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.4)',
                color: 'white',
                backdropFilter: 'blur(4px)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              <NavigateNextIcon />
            </IconButton>
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0, 0, 0, 0.4)',
                color: 'white',
                backdropFilter: 'blur(4px)',
                px: 2,
                py: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="caption" fontWeight="medium" noWrap sx={{ flex: 1, mr: 2 }}>
                {name}
                <Box component="span" sx={{ opacity: 0.7 }}>
                  {ext}
                </Box>
              </Typography>
              <Typography variant="caption">
                {currentSlide} / {maxSlides}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <CardContent>
        <Stack spacing={1}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h6" component="h2" noWrap sx={{ flex: 1, mr: 1 }}>
              {name}
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {ext}
              </Box>
            </Typography>
            <IconButton
              size="small"
              onClick={onMoreClick}
              sx={{ mt: -0.5, mr: -0.5 }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
          {file.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {file.description}
            </Typography>
          )}
          {/* Status + Tags Row */}
          <Box
            display="flex"
            alignItems="center"
            sx={{
              flexWrap: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <Chip
              label={file.public ? (lang === 'zh-CN' ? '公共模板' : 'Published') : (lang === 'zh-CN' ? '私有' : 'Draft')}
              size="small"
              color={file.public ? 'success' : 'default'}
              sx={{ height: 24, mr: 0.5, flexShrink: 0 }}
            />
            {tagNames.slice(0, 2).map((tag, idx) => (
              <Chip
                key={`tag-${idx}`}
                label={tag}
                size="small"
                sx={{
                  mr: 0.5,
                  height: 24,
                  maxWidth: 120,
                  '.MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    px: 1,
                  },
                }}
              />
            ))}
            {tagNames.length > 2 && (
              <Chip
                label={`+${tagNames.length - 2}`}
                size="small"
                sx={{ height: 24, flexShrink: 0 }}
              />
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default SlideshowCard; 